import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import type { RootState } from "@js/store";
import type { EmployerBidListBid } from "@js/types/jobs";

import { isPublicBidWithoutUser } from "../../helpers";

type BidBulkSelectionSlice = {
  selectedBidIds: number[];
};

const initialState: BidBulkSelectionSlice = {
  selectedBidIds: [],
};

const bidBulkSelectionSlice = createSlice({
  name: "bidBulkSelectionSlice",
  initialState,
  reducers: {
    syncSelectedBidIds(state, action: PayloadAction<number[]>) {
      const currentBidIds = action.payload;

      state.selectedBidIds = state.selectedBidIds.filter((id) =>
        currentBidIds.includes(id),
      );
    },

    selectBids(state, action: PayloadAction<number[]>) {
      state.selectedBidIds = action.payload;
    },

    deselectAllBids(state) {
      state.selectedBidIds = [];
    },

    resetSelectedBids(state) {
      state.selectedBidIds = [];
    },

    selectBid(state, action: PayloadAction<number>) {
      const id = action.payload;

      if (!state.selectedBidIds.includes(id)) {
        state.selectedBidIds.push(id);
      }
    },

    deselectBid(state, action: PayloadAction<number>) {
      const id = action.payload;

      state.selectedBidIds = state.selectedBidIds.filter(
        (selectedId) => selectedId !== id,
      );
    },
  },
});

export const {
  syncSelectedBidIds,
  selectBids,
  deselectAllBids,
  resetSelectedBids,
  selectBid,
  deselectBid,
} = bidBulkSelectionSlice.actions;

export const getSelectedBidIds = (state: RootState) =>
  state.bidBulkSelection.selectedBidIds;

export const getHiddenUnselectedBidsTotal = (
  bidList: EmployerBidListBid[],
  selectedBidIds: number[],
) => {
  return bidList.filter(
    (bid) => bid.is_hidden && !selectedBidIds.includes(bid.id),
  ).length;
};

export const canBeInterviewed = (bid: EmployerBidListBid) =>
  ["new", "viewed"].includes(bid.status) && !isPublicBidWithoutUser(bid);

export const canBeRejected = (
  bid: EmployerBidListBid,
  selectedBidIds: number[],
) =>
  selectedBidIds.includes(bid.id) &&
  !["hired", "rejected"].includes(bid.status);

export const getIsAnyOfBidsUnapproved = (bids: EmployerBidListBid[]) => {
  return bids.some(
    (bid) => !(bid.freelancer.approved || bid.can_send_interview_request),
  );
};

export default bidBulkSelectionSlice.reducer;
